import { Checkbox as BaseCheckbox } from "@base-ui/react/checkbox";
import { CheckboxGroup as BaseCheckboxGroup } from "@base-ui/react/checkbox-group";
import { Check, Minus } from "lucide-react";
import type { FunctionComponent, ReactNode } from "react";
import { cn } from "./cn";
import { FOCUS_RING } from "./focus";

export interface CheckboxProps extends Omit<
  BaseCheckbox.Root.Props,
  "className" | "onChange" | "onCheckedChange" | "render" | "children"
> {
  /** Receives the new checked state (not an event, unlike antd's `onChange(e)`). */
  onChange?: (checked: boolean) => void;
  /** Label rendered to the right of the box. */
  children?: ReactNode;
  className?: string;
}

/** Checkbox with an optional label; the whole row is clickable. */
export const Checkbox: FunctionComponent<CheckboxProps> = ({ onChange, children, className, disabled, ...props }) => {
  // A present-but-undefined `checked` (an empty RHF field) means controlled-off, not "switch to uncontrolled".
  const checkedProps = "checked" in props ? { checked: props.checked ?? false } : {};

  return (
    <label
      className={cn(
        "inline-flex cursor-pointer items-center gap-2 text-sm text-noa",
        disabled && "cursor-not-allowed text-noa-disabled",
        className
      )}
    >
      <BaseCheckbox.Root
        onCheckedChange={(checked) => onChange?.(checked)}
        disabled={disabled}
        className={cn(
          "flex size-4 shrink-0 items-center justify-center rounded border border-noa-input bg-noa transition-colors",
          "hover:border-noa-primary-hover data-[checked]:border-noa-primary-strong data-[checked]:bg-noa-primary",
          "data-[indeterminate]:border-noa-primary-strong data-[indeterminate]:bg-noa-primary",
          FOCUS_RING,
          "data-[disabled]:border-noa data-[disabled]:bg-noa-hover"
        )}
        {...props}
        {...checkedProps}
      >
        <BaseCheckbox.Indicator className="flex text-noa-on-primary data-[disabled]:text-noa-disabled">
          {props.indeterminate ? (
            <Minus className="size-3" strokeWidth={3} />
          ) : (
            <Check className="size-3" strokeWidth={3} />
          )}
        </BaseCheckbox.Indicator>
      </BaseCheckbox.Root>
      {children !== undefined && <span>{children}</span>}
    </label>
  );
};

export interface CheckboxOption {
  label: ReactNode;
  value: string;
  disabled?: boolean;
}

export interface CheckboxGroupProps {
  options: (string | CheckboxOption)[];
  value?: string[];
  defaultValue?: string[];
  onChange?: (value: string[]) => void;
  disabled?: boolean;
  className?: string;
  id?: string;
}

/** Horizontal set of checkboxes bound to a `string[]` (antd `Checkbox.Group`). */
export const CheckboxGroup: FunctionComponent<CheckboxGroupProps> = ({
  options,
  onChange,
  className,
  disabled,
  ...props
}) => (
  <BaseCheckboxGroup
    onValueChange={(value) => onChange?.(value)}
    disabled={disabled}
    className={cn("inline-flex flex-wrap items-center gap-x-4 gap-y-2", className)}
    {...props}
  >
    {options.map((option) => {
      const {
        label,
        value,
        disabled: optionDisabled,
      } = typeof option === "string" ? { label: option, value: option, disabled: false } : option;
      return (
        <Checkbox key={value} value={value} disabled={disabled || optionDisabled}>
          {label}
        </Checkbox>
      );
    })}
  </BaseCheckboxGroup>
);
